import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Lightbulb, RefreshCw, Zap, ArrowRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

const ENGINE_INFO = {
  openai: { 
    name: "GPT-4o", 
    color: "#10B981",
    description: "Balanced, versatile suggestions"
  },
  anthropic: { 
    name: "Claude", 
    color: "#8B5CF6",
    description: "Thoughtful, nuanced feedback"
  },
  gemini: { 
    name: "Gemini", 
    color: "#3B82F6",
    description: "Creative, unexpected angles"
  }
};

const REQUEST_TYPES = [
  { value: "improve", label: "Improve", icon: Sparkles, description: "Get suggestions to make the joke land better" },
  { value: "alternative_punchlines", label: "Alt Punchlines", icon: Zap, description: "Generate different punchline options" },
  { value: "tags", label: "Add Tags", icon: ArrowRight, description: "Get extra punchlines to extend the laugh" },
  { value: "expand", label: "Expand Bit", icon: Lightbulb, description: "Turn a premise into a fuller bit" },
];

export const AIBrainstorm = ({ isOpen, onClose, joke }) => {
  const [selectedEngines, setSelectedEngines] = useState(["openai", "anthropic", "gemini"]);
  const [requestType, setRequestType] = useState("improve");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const toggleEngine = (engine) => {
    setSelectedEngines(prev => {
      if (prev.includes(engine)) {
        if (prev.length === 1) return prev; // Keep at least one
        return prev.filter(e => e !== engine);
      }
      return [...prev, engine];
    });
  };

  const handleBrainstorm = async () => {
    if (!joke) {
      toast.error("No joke selected");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const response = await fetch(`${API}/ai/brainstorm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          joke_id: joke.joke_id,
          premise: joke.premise,
          setup: joke.setup,
          punchline: joke.punchline,
          request_type: requestType,
          engines: selectedEngines
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        toast.error("Failed to get AI suggestions");
      }
    } catch (error) {
      toast.error("Failed to get AI suggestions");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] h-[90vh] max-h-[90vh] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-wider flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Brainstorm
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Get suggestions from multiple AI engines to refine your material.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-6 mt-4">
          {/* Left Column - Joke & Controls */}
          <div className="col-span-1 space-y-4">
            {/* Current Joke */}
            <div className="p-4 bg-muted/50 rounded-sm border border-border">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your Joke</h4>
              {joke ? (
                <div className="space-y-2">
                  <p className="font-mono text-xs text-muted-foreground">{joke.premise}</p>
                  <p className="font-mono text-sm">{joke.setup}</p>
                  <p className="font-mono text-sm font-bold text-primary">{joke.punchline}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No joke selected</p>
              )}
            </div>

            {/* Request Type */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                What do you need?
              </Label>
              <div className="space-y-2">
                {REQUEST_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setRequestType(type.value)}
                      className={`w-full p-3 rounded-sm border text-left transition-colors ${
                        requestType === type.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`request-type-${type.value}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${requestType === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="font-medium text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Engine Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                AI Engines
              </Label>
              <div className="space-y-2">
                {Object.entries(ENGINE_INFO).map(([key, info]) => (
                  <div 
                    key={key}
                    className="flex items-center space-x-2 p-2 rounded-sm hover:bg-muted/50"
                  >
                    <Checkbox
                      id={`engine-${key}`}
                      checked={selectedEngines.includes(key)}
                      onCheckedChange={() => toggleEngine(key)}
                      data-testid={`engine-checkbox-${key}`}
                    />
                    <label 
                      htmlFor={`engine-${key}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: info.color }}
                        />
                        <span className="font-medium text-sm">{info.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{info.description}</p>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Brainstorm Button */}
            <Button 
              onClick={handleBrainstorm}
              disabled={isLoading || !joke || selectedEngines.length === 0}
              className="w-full bg-primary text-primary-foreground font-semibold uppercase tracking-wider hover:bg-spotlight-hover"
              data-testid="brainstorm-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Thinking...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Suggestions
                </>
              )}
            </Button>
          </div>

          {/* Right Column - Results */}
          <div className="col-span-2">
            <ScrollArea className="h-[500px] pr-4">
              {!results && !isLoading && (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="font-display text-lg uppercase mb-2">Ready to Brainstorm</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Select what kind of help you need and click "Get Suggestions" to see AI perspectives on your joke.
                  </p>
                </div>
              )}

              {isLoading && (
                <div className="flex flex-col items-center justify-center h-full">
                  <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                  <p className="text-muted-foreground">Getting perspectives from {selectedEngines.length} AI engines...</p>
                </div>
              )}

              {results && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {REQUEST_TYPES.find(t => t.value === results.request_type)?.label}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleBrainstorm}
                      disabled={isLoading}
                      data-testid="refresh-suggestions-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>

                  {results.suggestions.map((result, index) => (
                    <div 
                      key={result.engine}
                      className="p-4 bg-muted/30 rounded-sm border border-border"
                      data-testid={`suggestion-${result.engine}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: ENGINE_INFO[result.engine]?.color }}
                          />
                          <span className="font-semibold">{ENGINE_INFO[result.engine]?.name}</span>
                        </div>
                        {result.success && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(result.suggestion, index)}
                            className="h-7 px-2"
                            data-testid={`copy-suggestion-${result.engine}`}
                          >
                            {copiedIndex === index ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      
                      {result.success ? (
                        <div className="font-mono text-sm whitespace-pre-wrap leading-relaxed">
                          {result.suggestion}
                        </div>
                      ) : (
                        <div className="text-sm text-destructive">
                          Error: {result.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIBrainstorm;
